"use client"

import { useState, useEffect } from "react"
import { useApi, API_ENDPOINTS } from "@/lib/api"
import { useAtsSystemCode } from "@/hooks/use-ats-system-code"
import type { AtsInfo } from "@/types/ats-integration-api"
import type { AtsInfoState } from "@/types/ui"

export function useAtsInfo(atsSource: string | null): AtsInfoState {
  const { atsIntegrationApi } = useApi()
  const { getAtsSystemCode } = useAtsSystemCode()
  const [atsInfo, setAtsInfo] = useState<AtsInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAtsInfo = async (atsSource: string) => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Convert atsSource to atsSystem code
      const atsSystemCode = getAtsSystemCode(atsSource)
      
      // Call the info endpoint
      const response = await atsIntegrationApi.get<AtsInfo>(
        API_ENDPOINTS.ATS_INTEGRATION.INFO(atsSystemCode)
      )
      
      setAtsInfo(response)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ATS info'
      setError(errorMessage)
      console.error('Failed to fetch ATS info:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch ATS info when atsSource changes
  useEffect(() => {
    if (atsSource) {
      fetchAtsInfo(atsSource)
    } else {
      setAtsInfo(null)
      setError(null)
    }
  }, [atsSource])

  return {
    data: atsInfo,
    isLoading,
    error
  }
}
